import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, TrendingDown, TrendingUp, AlertTriangle, Users, X } from 'lucide-react';
import './ActivityFeedPanel.css';

const DUMMY_ACTIVITY = [
  {
    id: '1',
    user_display_name: 'Alice Chen',
    action_type: 'finding',
    summary: 'Revenue dropped 18% in the Midwest region last week — traced it to 3 distribution partners pausing orders.',
    workspace_name: 'Sales Analytics',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    detail: 'Partners #2041, #2087, and #2103 placed zero orders between Jan 20–27. Combined, they account for 22% of Midwest volume. Alice flagged this after running her Weekly Regional Breakdown report and cross-referencing with the partner activity table.',
  },
  {
    id: '2',
    user_display_name: 'David Kim',
    action_type: 'anomaly',
    summary: 'Customer acquisition cost spiked to $142 this month — nearly double the $78 average. Paid search is the driver.',
    workspace_name: 'Marketing Analytics',
    timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    detail: 'Google Ads CPC increased 40% after a competitor entered the same keyword set. Facebook remained stable at $61 CAC. David recommends shifting 20% of paid search budget to retargeting until CPCs normalize.',
  },
  {
    id: '3',
    user_display_name: 'Carol Zhang',
    action_type: 'insight',
    summary: 'Top 5% of customers generate 38% of revenue but only 12% of support tickets — high-value, low-maintenance segment.',
    workspace_name: 'Customer Success',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    detail: 'Carol identified 847 accounts with LTV > $12,000 and fewer than 2 tickets per year. These customers have an average retention rate of 94% vs. 71% company-wide. She suggests a dedicated "VIP" segment for upsell campaigns.',
  },
  {
    id: '4',
    user_display_name: 'Grace Patel',
    action_type: 'trend',
    summary: 'Return rate on electronics category climbed from 4.2% to 7.8% over the past quarter — mostly "not as described" complaints.',
    workspace_name: 'Operations',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    detail: 'The increase is concentrated in 3 SKUs added in November. Product listing images and specs were copied from the manufacturer and may not match the actual units shipped. Grace recommends a listing audit before the next restock.',
  },
  {
    id: '5',
    user_display_name: 'Frank Liu',
    action_type: 'finding',
    summary: 'Employee overtime hours are 35% above budget in fulfillment — coincides with the new packaging workflow rollout.',
    workspace_name: 'HR Analytics',
    timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    detail: 'The new 5-step packaging QC process added ~4 minutes per order. At current volume (1,200 orders/day), that is 80 extra labor hours per week. Frank suggests either adding a shift or streamlining the QC to 3 steps for standard items.',
  },
];

const ACTION_CONFIG = {
  finding: { icon: TrendingDown, color: '#DC2626', bg: '#FEE2E2', label: 'Finding' },
  anomaly: { icon: AlertTriangle, color: '#D97706', bg: '#FEF3C7', label: 'Anomaly' },
  insight: { icon: Users, color: '#3ECF8E', bg: '#E7F9EF', label: 'Insight' },
  trend: { icon: TrendingUp, color: '#3B82F6', bg: '#EFF6FF', label: 'Trend' },
};

function getRelativeTime(isoTimestamp) {
  const now = Date.now();
  const then = new Date(isoTimestamp).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(isoTimestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}


function ActivityFeedPanel() {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const expandedItem = expandedId ? DUMMY_ACTIVITY.find(a => a.id === expandedId) : null;

  return (
    <div className={`activity-panel ${collapsed ? 'activity-panel-collapsed' : ''}`}>
      <button
        className="activity-panel-toggle"
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? 'Show team activity' : 'Hide team activity'}
      >
        {collapsed ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
      </button>
      <div className="activity-panel-content">
        <div className="activity-panel-header">
          <h3>Team Activity</h3>
        </div>
        <div className="activity-panel-list">
          {DUMMY_ACTIVITY.map(item => {
            const config = ACTION_CONFIG[item.action_type] || ACTION_CONFIG.finding;
            const Icon = config.icon;
            return (
              <div
                key={item.id}
                className="activity-item"
                onClick={() => setExpandedId(item.id)}
              >
                <div className="activity-item-body">
                  <div className="activity-item-top">
                    <span className="activity-item-name">{item.user_display_name}</span>
                    <span className="activity-item-time">{getRelativeTime(item.timestamp)}</span>
                  </div>
                  <div className="activity-item-summary">{item.summary}</div>
                  <div className="activity-item-meta">
                    <span className="activity-item-type-badge" style={{ color: config.color, backgroundColor: config.bg }}>
                      <Icon size={11} />
                      {config.label}
                    </span>
                    <span className="activity-item-workspace">{item.workspace_name}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail overlay */}
      {expandedItem && (
        <div className="activity-detail-overlay" onClick={() => setExpandedId(null)}>
          <div className="activity-detail-card" onClick={(e) => e.stopPropagation()}>
            <button className="activity-detail-close" onClick={() => setExpandedId(null)}>
              <X size={16} />
            </button>
            <div className="activity-detail-header">
              <span className="activity-detail-name">{expandedItem.user_display_name}</span>
              <span className="activity-detail-time">{getRelativeTime(expandedItem.timestamp)}</span>
            </div>
            <div className="activity-detail-meta">
              {(() => {
                const config = ACTION_CONFIG[expandedItem.action_type] || ACTION_CONFIG.finding;
                const Icon = config.icon;
                return (
                  <span className="activity-item-type-badge" style={{ color: config.color, backgroundColor: config.bg }}>
                    <Icon size={11} />
                    {config.label}
                  </span>
                );
              })()}
              <span className="activity-item-workspace">{expandedItem.workspace_name}</span>
            </div>
            <p className="activity-detail-summary">{expandedItem.summary}</p>
            <p className="activity-detail-body">{expandedItem.detail}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityFeedPanel;
